// Tiny safe arithmetic expression engine for derived lab parameters.
//
// What it supports:
//   * numbers (integers + decimals)
//   * identifiers — parameter names, which may include letters, digits,
//     underscore, period, percent, hash and slash, e.g. HGB, LYM%, GRAN#,
//     a/G_Ratio. Names are matched against the keys in the `scope` object
//     passed to evaluate().
//   * bracketed identifiers — `[Cholesterol Ratio]`, `[Total - HDL]`, etc.
//     This lets parameter names contain spaces, hyphens, parentheses and
//     other characters the bare identifier rule rejects, without forcing
//     the user to rewrite established lab nomenclature. The brackets are
//     stripped after tokenization, so `[HGB]` and `HGB` resolve to the
//     same scope key.
//   * unary minus
//   * binary operators:  +  -  *  /  ^   (^ = power, right-associative)
//   * parentheses
//   * a whitelisted set of math functions — required for real clinical
//     formulas like eGFR (CKD-EPI) which uses min/max:
//       min(a, b)      max(a, b)
//       abs(x)         sqrt(x)
//       ln(x)          log(x)        log10(x)
//       exp(x)         round(x)
//       floor(x)       ceil(x)
//     The whitelist is fixed at parse time; any other identifier followed
//     by `(` is rejected. Functions can't read globals or trigger
//     side-effects — they're just calls into a hand-picked dispatch table.
//
// What it does NOT support — by design — so the engine can never be
// abused to run code or read globals:
//   * arbitrary function calls (only the whitelist above)
//   * variable assignment
//   * strings, booleans, comparisons, conditionals
//   * eval(), Function(), or any JS interop
//
// The flow is the standard shunting-yard algorithm with function-call
// extensions: function names are pushed onto the operator stack, commas
// pop until the matching `(`, and the closing `)` of a function call
// pops the function name back into the output queue.
//
//   tokenize(expr)  →  toRPN(tokens)  →  evalRPN(rpn, scope)
//
// All three steps throw a FormulaError with a short message on the first
// problem, so the UI can surface "needs HGB" or "division by zero"
// instead of crashing.

export class FormulaError extends Error {
  constructor(message, kind = 'invalid') {
    super(message);
    this.name = 'FormulaError';
    this.kind = kind;
  }
}

// ── Tokenizer ────────────────────────────────────────────────────────────
// Characters we accept inside identifiers. Parameter names like LYM% and
// GRAN# show up in real labs, so they're allowed even though most parsers
// would reject them.
//
// `/` is deliberately NOT in this character class. It needs context-aware
// handling: a slash followed by a letter/underscore is part of an
// identifier (so `a/G_Ratio` stays whole), but a slash followed by a
// digit or whitespace is a division operator (so `Creatinine/0.9` parses
// as `Creatinine / 0.9`). That logic lives in the identifier scanner
// loop below.
const IDENT_CHARS = /[A-Za-z0-9_.%#]/;
const DIGIT = /[0-9]/;

function tokenize(expr) {
  const out = [];
  const s = String(expr ?? '');
  let i = 0;
  while (i < s.length) {
    const c = s[i];

    // whitespace — skip
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }

    // number — integer + optional fraction
    if (DIGIT.test(c) || (c === '.' && DIGIT.test(s[i + 1] || ''))) {
      let j = i;
      let dot = c === '.';
      j++;
      while (j < s.length) {
        const cj = s[j];
        if (DIGIT.test(cj)) { j++; continue; }
        if (cj === '.' && !dot) { dot = true; j++; continue; }
        break;
      }
      out.push({ type: 'num', value: Number(s.slice(i, j)) });
      i = j;
      continue;
    }

    // identifier — letters/digits/_./%#, must START with a letter or _.
    // If the next non-whitespace character is `(`, this is a function call
    // (e.g. `min(...)`). Emit a 'func' token instead of an 'ident' so the
    // shunting-yard knows to push it onto the operator stack and look for
    // a matching `(`. Anything that isn't on the FUNCS whitelist is left
    // as an identifier so it surfaces as "Missing value for foo(" further
    // down the pipeline — but in practice we reject unknown function names
    // explicitly in evalRPN for a clearer error message.
    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1;
      while (j < s.length) {
        const cj = s[j];
        if (IDENT_CHARS.test(cj)) { j++; continue; }
        // Context-aware `/`: stays inside the identifier only when the
        // very next character is also identifier-ish (letter or _), so
        // names like `a/G_Ratio` are absorbed whole. Anything else
        // (slash before a digit, a space, etc.) means division.
        if (cj === '/' && j + 1 < s.length && /[A-Za-z_]/.test(s[j + 1])) {
          j++;
          continue;
        }
        break;
      }
      const name = s.slice(i, j);
      // Peek past whitespace to detect a following `(`.
      let k = j;
      while (k < s.length && (s[k] === ' ' || s[k] === '\t')) k++;
      if (s[k] === '(' && FUNCS.has(name.toLowerCase())) {
        out.push({ type: 'func', value: name.toLowerCase() });
      } else {
        out.push({ type: 'ident', value: name });
      }
      i = j;
      continue;
    }

    // comma — argument separator inside function calls.
    if (c === ',') {
      out.push({ type: 'op', value: ',' });
      i++;
      continue;
    }

    // bracketed identifier — `[anything but ]]` lets the user reference
    // parameter names that contain spaces or characters the bare-identifier
    // rule above can't accept. We keep newlines out so a runaway `[` can't
    // swallow the rest of a multi-line formula.
    if (c === '[') {
      let j = i + 1;
      while (j < s.length && s[j] !== ']' && s[j] !== '\n') j++;
      if (j >= s.length || s[j] !== ']') {
        throw new FormulaError('Unclosed [ in formula');
      }
      const inner = s.slice(i + 1, j).trim();
      if (!inner) throw new FormulaError('Empty [] identifier');
      out.push({ type: 'ident', value: inner });
      i = j + 1;
      continue;
    }

    // operators + parens
    if ('+-*/^()'.includes(c)) {
      out.push({ type: 'op', value: c });
      i++;
      continue;
    }

    throw new FormulaError(`Unexpected character '${c}' in formula`);
  }
  return out;
}

// ── Function whitelist ──────────────────────────────────────────────────
// Mapping from function name (lower-cased) to { arity, fn }. The arity is
// either an exact number (e.g. 2 for min/max) or 'unary'. Any name not in
// this table is treated as a plain identifier by the tokenizer, so the
// pipeline never gets a chance to call something dangerous.
const FUNCS = new Map([
  ['min',   { arity: 2,       fn: (a, b) => Math.min(a, b) }],
  ['max',   { arity: 2,       fn: (a, b) => Math.max(a, b) }],
  ['abs',   { arity: 1,       fn: (a)    => Math.abs(a) }],
  ['sqrt',  { arity: 1,       fn: (a)    => Math.sqrt(a) }],
  ['ln',    { arity: 1,       fn: (a)    => Math.log(a) }],
  ['log',   { arity: 1,       fn: (a)    => Math.log10(a) }],
  ['log10', { arity: 1,       fn: (a)    => Math.log10(a) }],
  ['exp',   { arity: 1,       fn: (a)    => Math.exp(a) }],
  ['round', { arity: 1,       fn: (a)    => Math.round(a) }],
  ['floor', { arity: 1,       fn: (a)    => Math.floor(a) }],
  ['ceil',  { arity: 1,       fn: (a)    => Math.ceil(a) }],
]);

// ── Shunting-yard: infix → RPN ───────────────────────────────────────────
const PRECEDENCE = { '+': 2, '-': 2, '*': 3, '/': 3, 'u-': 5, '^': 4 };
const RIGHT_ASSOC = new Set(['^', 'u-']);

function toRPN(tokens) {
  const out = [];
  const stack = [];
  // "prevIsOperandish" lets us detect unary minus: a '-' is unary when the
  // previous token is null, an opening paren, or another operator.
  let prevIsOperandish = false;

  for (const tok of tokens) {
    if (tok.type === 'num' || tok.type === 'ident') {
      out.push(tok);
      prevIsOperandish = true;
      continue;
    }
    // Function token: push to the operator stack and wait for its
    // matching `(`. When we hit the closing `)` later we'll pop the
    // function back into the output queue.
    if (tok.type === 'func') {
      stack.push(tok);
      prevIsOperandish = false;
      continue;
    }
    if (tok.type === 'op') {
      if (tok.value === '(') {
        stack.push(tok);
        prevIsOperandish = false;
        continue;
      }
      if (tok.value === ')') {
        // Pop until we hit the matching `(`. Everything in between is
        // an operator/operand that lives inside the parentheses.
        while (stack.length && stack[stack.length - 1].value !== '(') {
          out.push(stack.pop());
        }
        if (!stack.length) throw new FormulaError('Unbalanced parenthesis');
        stack.pop(); // drop '('
        // If the token immediately before the `(` was a function, this
        // `)` ends the call — pop the function name into the output so
        // evalRPN can invoke it with the right number of operands.
        if (stack.length && stack[stack.length - 1].type === 'func') {
          out.push(stack.pop());
        }
        prevIsOperandish = true;
        continue;
      }
      if (tok.value === ',') {
        // Comma inside a function call: pop until the enclosing `(` so
        // each argument's RPN is complete before the next one starts.
        // A comma outside a function call is a syntax error.
        let foundOpen = false;
        while (stack.length) {
          const top = stack[stack.length - 1];
          if (top.value === '(') { foundOpen = true; break; }
          out.push(stack.pop());
        }
        if (!foundOpen) throw new FormulaError('Unexpected , in formula');
        prevIsOperandish = false;
        continue;
      }
      // unary minus
      let op = tok.value;
      if (op === '-' && !prevIsOperandish) op = 'u-';

      const myPrec = PRECEDENCE[op];
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.value === '(') break;
        // A function on the stack has no precedence — leave it alone
        // until its closing `)` triggers the pop.
        if (top.type === 'func') break;
        const topOp = top.value;
        const topPrec = PRECEDENCE[topOp];
        if (
          topPrec > myPrec ||
          (topPrec === myPrec && !RIGHT_ASSOC.has(op))
        ) {
          out.push(stack.pop());
        } else break;
      }
      stack.push({ type: 'op', value: op });
      prevIsOperandish = false;
    }
  }
  while (stack.length) {
    const t = stack.pop();
    if (t.value === '(' || t.value === ')') {
      throw new FormulaError('Unbalanced parenthesis');
    }
    out.push(t);
  }
  return out;
}

// ── RPN evaluator ────────────────────────────────────────────────────────
export function evalRPN(rpn, scope) {
  const st = [];
  const get = (name) => {
    if (!Object.prototype.hasOwnProperty.call(scope, name)) {
      throw new FormulaError(`Missing value for ${name}`, 'missing');
    }
    const v = scope[name];
    if (v === null || v === undefined || v === '') {
      throw new FormulaError(`Missing value for ${name}`, 'missing');
    }
    const n = Number(v);
    if (!Number.isFinite(n)) {
      throw new FormulaError(`Non-numeric value for ${name}`, 'non-numeric');
    }
    return n;
  };

  for (const tok of rpn) {
    if (tok.type === 'num') { st.push(tok.value); continue; }
    if (tok.type === 'ident') { st.push(get(tok.value)); continue; }
    if (tok.type === 'func') {
      const spec = FUNCS.get(tok.value);
      if (!spec) throw new FormulaError(`Unknown function ${tok.value}`);
      if (st.length < spec.arity) {
        throw new FormulaError(`${tok.value}() needs ${spec.arity} argument${spec.arity === 1 ? '' : 's'}`);
      }
      const args = [];
      for (let i = 0; i < spec.arity; i++) args.unshift(st.pop());
      const r = spec.fn(...args);
      if (!Number.isFinite(r)) {
        throw new FormulaError(`${tok.value}() result is not a finite number`, 'overflow');
      }
      st.push(r);
      continue;
    }

    const op = tok.value;
    if (op === 'u-') {
      if (!st.length) throw new FormulaError('Missing operand');
      st.push(-st.pop());
      continue;
    }
    if (st.length < 2) throw new FormulaError('Missing operand');
    const b = st.pop();
    const a = st.pop();
    let r;
    switch (op) {
      case '+': r = a + b; break;
      case '-': r = a - b; break;
      case '*': r = a * b; break;
      case '/':
        if (b === 0) throw new FormulaError('Division by zero', 'divzero');
        r = a / b;
        break;
      case '^': r = Math.pow(a, b); break;
      default: throw new FormulaError(`Unknown operator ${op}`);
    }
    if (!Number.isFinite(r)) {
      throw new FormulaError('Result is not a finite number', 'overflow');
    }
    st.push(r);
  }
  if (st.length !== 1) throw new FormulaError('Invalid formula');
  return st[0];
}

// ── Public API ───────────────────────────────────────────────────────────

/** Parse an expression into RPN. Throws FormulaError on syntax issues. */
export function parse(expr) {
  return toRPN(tokenize(expr));
}

/** Evaluate an expression against a scope object. Returns a number. */
export function evaluate(expr, scope = {}) {
  return evalRPN(parse(expr), scope);
}

/** Pull every identifier the formula references — for the "Inputs detected"
 *  hint at setup time and the "needs X" pending hint at result entry. */
export function extractIdentifiers(expr) {
  const tokens = tokenize(expr);
  const seen = new Set();
  for (const t of tokens) if (t.type === 'ident') seen.add(t.value);
  return Array.from(seen);
}

/** Format a derived result. Lab software conventionally clips to a small
 *  number of decimals depending on magnitude — keeps the report tidy. */
export function formatDerived(n) {
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toFixed(0);
  if (abs >= 100)  return n.toFixed(1);
  if (abs >= 1)    return n.toFixed(2);
  if (abs >= 0.01) return n.toFixed(3);
  return n.toFixed(4);
}

// ── Reference-range helpers ────────────────────────────────────────────────
// Lab ranges arrive as free-form strings written by humans, e.g.:
//   "13.5-17.5"  "0.5 - 1.7"  "<3.0"  "≤3.0"  ">1.0"  "≥40"  "3.5 to 5.0"
// `parseRange` normalises any of these to { lo, hi } with either side `null`
// when the bound is open. Returns null when the string is qualitative or
// can't be parsed numerically (e.g. "positive/negative", "trace").

const RANGE_LT = /^[<≤]\s*(-?\d*\.?\d+)/;
const RANGE_GT = /^[>≥]\s*(-?\d*\.?\d+)/;
const RANGE_HI_LO = /^(-?\d*\.?\d+)\s*(?:-|to|–|—)\s*(-?\d*\.?\d+)/i;

export function parseRange(rangeStr) {
  if (rangeStr === null || rangeStr === undefined) return null;
  const s = String(rangeStr).trim();
  if (!s || s === '-') return null;
  let m = s.match(RANGE_LT);
  if (m) return { lo: null, hi: parseFloat(m[1]) };
  m = s.match(RANGE_GT);
  if (m) return { lo: parseFloat(m[1]), hi: null };
  m = s.match(RANGE_HI_LO);
  if (m) {
    const lo = parseFloat(m[1]);
    const hi = parseFloat(m[2]);
    if (Number.isFinite(lo) && Number.isFinite(hi)) return { lo, hi };
  }
  return null;
}

/** Render a {lo, hi} pair back to a canonical range string. Decimal count
 *  scales with magnitude so a ratio range like 0.5-1.7 keeps 2-3 dp while
 *  WBC counts in the thousands round to whole numbers. */
export function formatRange({ lo, hi }) {
  const fmt = (n) => {
    if (n == null || !Number.isFinite(n)) return '';
    const abs = Math.abs(n);
    if (abs >= 100) return n.toFixed(1);
    if (abs >= 1)   return n.toFixed(2);
    return n.toFixed(3);
  };
  if (lo == null && hi == null) return '';
  if (lo == null) return `<${fmt(hi)}`;
  if (hi == null) return `>${fmt(lo)}`;
  return `${fmt(lo)}-${fmt(hi)}`;
}

/** Propagate a parameter's reference range through its formula.
 *
 *  Given a formula like `HGB / RBC * 10` and ranges
 *    HGB: { lo: 13.5, hi: 17.5 }, RBC: { lo: 4.5, hi: 6.0 }
 *  we evaluate the formula at every corner of the input hypercube
 *  (2^N combinations of lo/hi per input) and take min/max of the
 *  results. For positive-valued lab measurements the operators we
 *  support (+ − × ÷ ^) are monotone in each argument, so the min/max
 *  of the corners is the true min/max of the function over the box.
 *
 *  Returns null if any required input range is missing, the formula
 *  can't be evaluated cleanly at every corner, or there are more
 *  than 8 inputs (an arbitrary safety cap — typical lab formulas
 *  reference 2–4 inputs, the cap just keeps us from blowing up on
 *  pathological formulas).
 *
 *  Open-ended inputs (e.g. ">40") use the side that's defined for
 *  every corner — equivalent to assuming the patient is at the
 *  boundary, which is the most informative interpretation in a
 *  reference-range context.
 */
export function computeDerivedRange(formula, identifierRanges) {
  if (!formula) return null;
  let ids;
  try { ids = extractIdentifiers(formula); }
  catch (_) { return null; }
  if (ids.length === 0) return null;
  if (ids.length > 8) return null;

  const ranges = ids.map((name) => identifierRanges.get(name));
  if (ranges.some((r) => !r)) return null;

  const results = [];
  const total = 1 << ids.length;
  for (let mask = 0; mask < total; mask++) {
    const scope = {};
    let viable = true;
    for (let i = 0; i < ids.length; i++) {
      const bit = (mask >> i) & 1;
      const r = ranges[i];
      // Prefer the side picked by `bit`; fall back to the other side if
      // this range is open-ended on the chosen side.
      let val = bit ? r.hi : r.lo;
      if (val == null) val = bit ? r.lo : r.hi;
      if (val == null || !Number.isFinite(val)) { viable = false; break; }
      scope[ids[i]] = val;
    }
    if (!viable) continue;
    try {
      const v = evaluate(formula, scope);
      if (Number.isFinite(v)) results.push(v);
    } catch (_) { /* skip this corner */ }
  }
  if (results.length === 0) return null;
  return { lo: Math.min(...results), hi: Math.max(...results) };
}

/** Detect direct self-reference and direct cycles. Returns a list of
 *  cycles found, where each cycle is the list of parameter names that
 *  participate. Empty list = clean. */
export function findCycles(parameters) {
  // parameters = [{ name, formula }] — formula may be empty for independents
  const nameByLc = new Map();
  for (const p of parameters) {
    if (p?.name) nameByLc.set(String(p.name).toLowerCase(), p);
  }
  const deps = new Map(); // name -> [names]
  for (const p of parameters) {
    if (!p?.name) continue;
    const key = String(p.name).toLowerCase();
    if (!p.formula || !String(p.formula).trim()) {
      deps.set(key, []);
      continue;
    }
    let ids;
    try { ids = extractIdentifiers(p.formula); }
    catch (_) { ids = []; }
    deps.set(key, ids.map((n) => n.toLowerCase()));
  }

  const cycles = [];
  const WHITE = 0, GREY = 1, BLACK = 2;
  const colour = new Map();
  const stack = [];

  function dfs(node) {
    colour.set(node, GREY);
    stack.push(node);
    for (const nxt of deps.get(node) || []) {
      if (!deps.has(nxt)) continue; // identifier not declared yet — skip
      const c = colour.get(nxt) || WHITE;
      if (c === GREY) {
        // cycle: slice from where nxt first appears
        const idx = stack.indexOf(nxt);
        cycles.push(stack.slice(idx).concat([nxt]));
      } else if (c === WHITE) {
        dfs(nxt);
      }
    }
    stack.pop();
    colour.set(node, BLACK);
  }
  for (const node of deps.keys()) {
    if ((colour.get(node) || WHITE) === WHITE) dfs(node);
  }
  return cycles;
}
